import { createContext, useCallback, useEffect, useMemo, useState } from 'react'

import { getActiveLanguage } from 'react-localize-redux'
import { connect } from 'react-redux'
import moment from 'moment'
import { isArray, isNumber } from 'lodash'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'

import { getUser } from '../reducers/UserReducer'
import { getAllEventCodes } from '../utils/api/eventCode'
import { getAllLanguages } from '../utils/api/language'
import { getUserOptins, getUserUnreadMessages } from '../utils/api/user'
import { getConstants } from '../utils/api/global'
import { arrayToObject, getEnvironment, recursiveIncludes } from '../utils'
import {
  InstitutionRoles,
  SchoolRoles,
  OtherRoles,
  Contexts,
  InstitutionOptions,
  LinkRequestStates
} from '../utils/constants'
import { CARPOOL_OPTIN_TEXTS, CARPOOL_TYPE, DATA_SHARING_TEXTS, DATA_SHARING_TYPE, UNSUPPORTED_TYPE } from '../utils/entities/optin'
import { getAllAreas } from '../utils/api/area'
import { resolvePromises } from '../utils/apiHelper'
import { useLocalStorage } from '../hooks/UseLocalStorage'
import LOCAL_STORAGE_KEYS from '../utils/localStorage'
import { Routes } from '../routes'
import { Environments } from '../config/environments'
import { reduceBySection, sortBySectionAndYear } from '../utils/entities/sectionToYear'
import { getAllLinkRequests } from '../utils/api/linkRequest'
import { getAphmAnomaliesSummary } from '../utils/api/aphmAnomaly'
import { getLastDetectedAt } from '../utils/entities/aphmAnomaly'
import { sortByAbbreviation } from '../utils/entities/schoolSection'
import { isAdmin, isCoordinator, isEncoder, isHospitalAdmin, isInstitutionUser, isSchoolUser } from '../utils/roles'
import { sortByName } from '../utils/entities/institution'

const mapStateToProps = state => ({
  currentLanguage: getActiveLanguage(state.locale).code,
  user: getUser(state.getUser)
})

const INVALID_STATUS = 0
const VALID_STATUS = 1

export const GlobalContext = createContext()

const GlobalProvider = ({ children, user }) => {
  const history = useHistory()

  const [countries, setCountries] = useState([])
  const [currentDate, setCurrentDate] = useState(moment())
  /** The care units every institution is seeded with : what a student is offered for a place that is not registered. */
  const [defaultSectorNames, setDefaultSectorNames] = useState([])
  const [eventCodes, setEventCodes] = useState({ data: [], status: VALID_STATUS })
  const [institutions, setInstitutions] = useState([])
  const [isDraft, setIsDraft] = useState(false)
  const [languages, setLanguages] = useState([])
  const [optins, setOptins] = useState([])
  const [pauses, setPauses] = useState([])
  const [schoolYears, setSchoolYears] = useState([])
  const [sections, setSections] = useState([])
  const [sectionToYears, setSectionToYears] = useState([])
  const [studentMetadata, setStudentMetadata] = useState([])
  const [tutorials, setTutorials] = useState([])
  const [unSeenChangelogs, setUnseenChangelogs] = useState([])
  const [unreadMessages, setUnreadMessages] = useState(null)
  const [areas, setAreas] = useState([])
  const [loading, setLoading] = useState(true)
  const [modules, setModules] = useState({
    [InstitutionOptions.RECRUITMENT_MODULE]: false,
    [InstitutionOptions.MODULE_SETTINGS]: false,
    [InstitutionOptions.APHM_ANOMALIES]: false
  })
  const [pendingLinkRequests, setPendingLinkRequests] = useState([])
  const [aphmAnomaliesSummary, setAphmAnomaliesSummary] = useState(null)
  const [aphmAnomaliesSeen, setAphmAnomaliesSeen] = useLocalStorage(LOCAL_STORAGE_KEYS.APHM_ANOMALIES_VIEW.SEEN_UNTIL, {})

  const betaModeKey = user?.id ? `${LOCAL_STORAGE_KEYS.MISC.BETA_MODE}.${user.id}` : null
  const [betaMode, setBetaMode] = useState(false)

  const canUseBetaMode = isSchoolUser(user) || isInstitutionUser(user)

  const WIPZone = useMemo(() => getEnvironment() !== Environments.PROD, [])
  const context = useMemo(() => user?.context ?? Contexts.NONE, [user?.context])
  const currentRole = useMemo(() => user?.roles?.[0] ?? OtherRoles.UNKNOWN, [user?.roles])
  const isReadOnly = useMemo(() => Array.isArray(user?.roles) && recursiveIncludes(user?.roles, [InstitutionRoles.OBSERVER, SchoolRoles.DOMAIN_EVALUATOR]), [user?.roles])
  const sectionsObject = useMemo(() => arrayToObject(sections, 'id'), [sections])
  const sectionToYearsBySection = useMemo(() => reduceBySection(sectionToYears), [sectionToYears])
  const newSectionToYears = useMemo(() => sectionToYears.filter(sty => sty.section !== null), [sectionToYears])
  const permissions = useMemo(() => ({
    // Only used for the password reset of STUDENTS (students table). A student belongs to their
    // school, never to the institution hosting them: the backend (UserVoter::RESET_PASSWORD) denies
    // that reset to an institution admin, so the button is only opened to school admins.
    canResetPassword: isAdmin(user) || isEncoder(user),
    canEditStudent: isEncoder(user) || isCoordinator(user)
  }), [user?.roles])

  useEffect(() => { getAllLanguages().then(setLanguages) }, [])

  useEffect(() => {
    if (user?.id) {
      getAllAreas(user).then(json => {
        if (json?.data) {
          setAreas(json.data)
        }
      })
    }
  }, [user.id])

  useEffect(() => {
    if (user?.id) {
      setLoading(true)

      const p1 = getConstants(user).then(json => {
        if (json?.data) {
          setEventCodes({ data: json.data.eventCodes, status: VALID_STATUS })
          setInstitutions(json.data.institutions.sort(sortByName))
          setOptins(getOptinsType(json.data.optins))
          setPauses(json.data.pauses)
          setSections(json.data.sections.sort(sortByAbbreviation))
          setSectionToYears(json.data.sectionToYears.sort(sortBySectionAndYear))
          setCountries(json.data.countries)
          setDefaultSectorNames(isArray(json.data.defaultSectorNames) ? json.data.defaultSectorNames : [])
          setSchoolYears(json.data.schoolYears)
          setStudentMetadata(json.data.studentMetadata)
          setTutorials(getTutorialsByUrl(json.data.tutorials))

          if (!user.roles.includes(OtherRoles.ADMIN)) {
            setUnseenChangelogs(json.data.changelogs)
          }
        }
      })
      const p2 = getUserUnreadMessages(user).then(json => {
        if (json && isNumber(json.data)) {
          setUnreadMessages(json.data)
        }
      })

      resolvePromises([p1, p2]).then(() => setLoading(false))
    }

    return () => {
      setLoading(true)
    }
  }, [user.id])

  useEffect(() => {
    if (eventCodes.status === INVALID_STATUS) {
      getAllEventCodes(user).then(data => setEventCodes({ data, status: VALID_STATUS }))
    }
  }, [user.id, eventCodes.status])

  const getTutorialsByUrl = tutorials => {
    const tutorialsByUrl = {}

    tutorials.forEach(t => {
      tutorialsByUrl[t.link] = t
    })

    return tutorialsByUrl
  }

  const getOptinsType = optins => {
    return optins.map(o => {
      if (DATA_SHARING_TEXTS.includes(o.text)) {
        o.type = DATA_SHARING_TYPE
      } else if (CARPOOL_OPTIN_TEXTS.includes(o.text)) {
        o.type = CARPOOL_TYPE
      } else {
        o.type = UNSUPPORTED_TYPE
      }

      return o
    })
  }

  const fetchOptins = user => {
    return getUserOptins(user).then(json => {
      if (json?.data) {
        setOptins(getOptinsType(json.data))
      }
    })
  }

  useEffect(() => {
    setBetaMode(betaModeKey !== null && JSON.parse(window.localStorage.getItem(betaModeKey)) === true)
  }, [betaModeKey])

  const toggleBetaMode = () => {
    setBetaMode(!betaMode)
    window.localStorage.setItem(betaModeKey, JSON.stringify(!betaMode))

    if (betaMode) {
      history.push(Routes.INTERNSHIPS)
    }
  }

  useEffect(() => {
    if (user?.id && (isEncoder(user) || isHospitalAdmin(user))) {
      getAllLinkRequests(user).then(({ data }) => setPendingLinkRequests(data.filter(lr => lr.status === LinkRequestStates.PENDING)))
    }
  }, [user?.id])

  useEffect(() => {
    if (user?.id && isHospitalAdmin(user) && modules[InstitutionOptions.APHM_ANOMALIES]) {
      getAphmAnomaliesSummary(user).then(json => {
        if (json?.data) { setAphmAnomaliesSummary({ ...json.data }) }
      })
    } else {
      setAphmAnomaliesSummary(null)
    }
  }, [user?.id, modules[InstitutionOptions.APHM_ANOMALIES]])

  const aphmAnomaliesSeenUntil = useMemo(() => aphmAnomaliesSeen?.[user?.id] ?? null, [aphmAnomaliesSeen, user?.id])

  const markAphmAnomaliesSeen = useCallback(
    () => setAphmAnomaliesSeen(previous => ({ ...previous, [user.id]: getLastDetectedAt(aphmAnomaliesSummary) })),
    [user?.id, aphmAnomaliesSummary, setAphmAnomaliesSeen]
  )

  return (
    <GlobalContext.Provider
      value={{
        betaMode: { active: canUseBetaMode && betaMode, available: canUseBetaMode, toggle: toggleBetaMode },
        context,
        countries,
        currentDate,
        currentRole,
        isDraft,
        isReadOnly,
        eventCodes: eventCodes.data,
        invalidateEventCodes: () => setEventCodes({ ...eventCodes, status: INVALID_STATUS }),
        defaultSectorNames,
        institutions,
        languages,
        pendingLinkRequests,
        aphmAnomaliesSummary,
        aphmAnomaliesSeenUntil,
        markAphmAnomaliesSeen,
        modules,
        newSectionToYears,
        optins,
        pauses,
        permissions,
        schoolYears,
        sections,
        sectionsObject,
        sectionToYears,
        sectionToYearsBySection,
        studentMetadata,
        tutorials,
        unreadMessages,
        unSeenChangelogs,
        user,
        WIPZone,
        loading,
        fetchOptins,
        setCurrentDate,
        setInstitutions,
        setIsDraft,
        setModules,
        setAphmAnomaliesSummary,
        setPendingLinkRequests,
        setUnreadMessages,
        setUnseenChangelogs,
        setTutorials,
        areas
      }}
    >
      {children}
    </GlobalContext.Provider>
  )
}

export default connect(mapStateToProps)(GlobalProvider)
